import { z } from 'zod';

import { IsoDateTimeSchema } from './common.js';
import { HotelCancellationPolicySchema, HotelRoomOccupancyInputSchema } from './hotel-booking.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * TBO Hotel provider (read-only mirror + live search).
 *
 * Local mirror table is `tbo_hotels` (Phase B1.5). It is drained per-city
 * from TBOHotelCodeList + Hoteldetails. The picker on the itinerary editor
 * binds to the mirror for fast typeahead; live availability/pricing is
 * looked up just-in-time via `POST /tbo-hotel/search`.
 */

// ---- Picker list (mirror-backed) ----------------------------------------

export const TboHotelListItemSchema = z.object({
  hotelCode: z.string(),
  name: z.string(),
  starRating: z.number().int().nullable(),
  address: z.string().nullable(),
  /** Reverse-geocoded neighborhood name (Nominatim). Null when not yet geocoded. */
  neighborhoodName: z.string().nullable().default(null),
  tboCityId: z.string(),
  countryCode: z.string().nullable(),
  /** Cover photo URL or null. */
  coverPhotoUrl: z.string().nullable(),
  syncedAt: IsoDateTimeSchema,
});
export type TboHotelListItem = z.infer<typeof TboHotelListItemSchema>;

export const TboHotelListQuerySchema = PaginationQuerySchema.extend({
  /** Required when searching the mirror - TBO hotels are city-scoped. */
  tboCityId: z.string().trim().min(1).optional(),
  /** Restrict to a country (ISO code as TBO carries it). */
  countryCode: z.string().trim().min(2).max(8).optional(),
  /** Filter by minimum star rating. */
  minStarRating: z.coerce.number().int().min(1).max(5).optional(),
});
export type TboHotelListQuery = z.infer<typeof TboHotelListQuerySchema>;

export const TboHotelListResponseSchema = paginatedResponseSchema(TboHotelListItemSchema);
export type TboHotelListResponse = z.infer<typeof TboHotelListResponseSchema>;

// ---- Detail (mirror; live extras returned by the live search endpoint) --

export const TboHotelDetailSchema = TboHotelListItemSchema.extend({
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  phone: z.string().nullable(),
  category: z.string().nullable(),
  amenities: z.array(z.string()),
  photos: z.array(z.string()),
});
export type TboHotelDetail = z.infer<typeof TboHotelDetailSchema>;

/**
 * Live hotel content fetched on demand (not the mirror) - powers the rich hotel
 * card: real name, photos, address/landmark, amenities, rating.
 */
export const TboHotelLiveDetailSchema = z.object({
  hotelCode: z.string(),
  name: z.string(),
  starRating: z.number().int().nullable(),
  address: z.string().nullable(),
  /** Reverse-geocoded neighborhood name from the mirror. */
  neighborhoodName: z.string().nullable().default(null),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  amenities: z.array(z.string()),
  photos: z.array(z.string()),
});
export type TboHotelLiveDetail = z.infer<typeof TboHotelLiveDetailSchema>;

// ---- Sync trigger (admin-only) ------------------------------------------

/** Default hotels per `POST /tbo-hotel/sync` request. Keeps each chunk
 * within browser/nginx/ALB timeouts; the admin client loops until `done`. */
export const TBO_HOTEL_SYNC_CHUNK_SIZE = 25;
export const TBO_HOTEL_SYNC_CHUNK_MAX = 50;

export const TboHotelSyncRequestSchema = z.object({
  /** TBO country code (alpha-2). Hotel sync is city-scoped, but the sync
   * service walks every city under a country in one batch. */
  countryCode: z.string().trim().min(2).max(8),
  /** Optional: restrict to a single TBO cityId. When omitted, every TBO
   * city under the country is iterated. */
  tboCityId: z.string().trim().min(1).optional(),
  /** Offset into the current city's hotel-code list (advanced via `nextOffset`). */
  offset: z.coerce.number().int().min(0).optional().default(0),
  /** Hotels to process this request (max 50). Default 25 fits default timeouts. */
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(TBO_HOTEL_SYNC_CHUNK_MAX)
    .optional()
    .default(TBO_HOTEL_SYNC_CHUNK_SIZE),
  /** When syncing a whole country, which city row to process. Advanced via
   * `nextCityIndex` once the current city's codes are exhausted. */
  cityIndex: z.coerce.number().int().min(0).optional().default(0),
});
export type TboHotelSyncRequestInput = z.input<typeof TboHotelSyncRequestSchema>;
/** Parsed request (defaults applied). Used by the API after Zod validation. */
export type TboHotelSyncRequest = z.output<typeof TboHotelSyncRequestSchema>;

export const TboHotelSyncResponseSchema = z.object({
  cities: z.number().int(),
  /** Total hotel codes in the city currently being processed (capped at 500). */
  hotels: z.number().int(),
  /** Codes processed in this request only. */
  processed: z.number().int(),
  upserted: z.number().int(),
  errors: z.number().int(),
  syncedAt: IsoDateTimeSchema,
  /** False while more chunks remain for the requested scope. */
  done: z.boolean(),
  /** Next `offset` for the same city, or null when moving to the next city. */
  nextOffset: z.number().int().nullable(),
  /** Next `cityIndex` when `nextOffset` is null and more cities remain. */
  nextCityIndex: z.number().int().nullable(),
  /** TBO city id for the chunk just processed (progress display). */
  currentCityId: z.string().nullable(),
});
export type TboHotelSyncResponse = z.infer<typeof TboHotelSyncResponseSchema>;

export const TboHotelSyncStatusSchema = z.object({
  hotels: z.number().int(),
  /** Most-recent synced_at across the mirror. */
  lastSyncedAt: IsoDateTimeSchema.nullable(),
});
export type TboHotelSyncStatus = z.infer<typeof TboHotelSyncStatusSchema>;

// ---- Live search (skips the mirror) -------------------------------------

export const TboPaxRoomSchema = z.object({
  adults: z.number().int().min(1).max(9),
  children: z.number().int().min(0).max(9).default(0),
  /** Each child age 0-17. Required by TBO's contract whenever children > 0. */
  childAges: z.array(z.number().int().min(0).max(17)).default([]),
});
export type TboPaxRoom = z.infer<typeof TboPaxRoomSchema>;

export const TboHotelLiveSearchInputSchema = z.object({
  /** Single TBO cityId. Required. */
  tboCityId: z.string().trim().min(1),
  /** Extra TBO cityIds mapped to the same City (nearby regions). Hotel codes are
   *  gathered across `tboCityId` + these and deduped, so the search returns the
   *  merged inventory (e.g. Ubud + Payangan). */
  additionalTboCityIds: z.array(z.string().trim().min(1)).max(20).optional(),
  checkIn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
  checkOut: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
  paxRooms: z.array(TboPaxRoomSchema).min(1).max(8),
  /** ISO-2 of the guest's nationality. Defaults to "IN". */
  guestNationality: z.string().trim().length(2).default('IN'),
  /** Optional: restrict the live search to specific hotel codes (e.g.
   * the mirror's hits that the picker already showed the user). */
  hotelCodes: z.array(z.string()).max(200).optional(),
  /** Currency the prices should be returned in (converted live). Default INR. */
  currencyCode: z.string().trim().length(3).default('INR'),
  /** Audit linkage: the lead this search is attributed to (optional). */
  leadId: z.string().uuid().optional(),
  /** Audit linkage: the itinerary this search is attributed to (optional). */
  itineraryId: z.string().uuid().optional(),
  /** Where the search originated, for audit attribution. SYSTEM is server-only,
   *  so it is intentionally excluded from this client-facing enum. */
  source: z.enum(['MANUAL_PICKER', 'AUTOFILL', 'REPRICE']).optional(),
});
export type TboHotelLiveSearchInput = z.infer<typeof TboHotelLiveSearchInputSchema>;

/**
 * One categorised landmark near a property, with how far it is.
 *
 * Shared by the results card and the property page: the card shows the two or
 * three nearest as chips, the page lists them in full. Same shape either way —
 * two copies of it drifted the moment one of them gained a field.
 */
export const HotelNearestLandmarkSchema = z.object({
  name: z.string(),
  /** `DISTRICT` | `ATTRACTION` | `MALL` | `AIRPORT` | `BEACH` | `LANDMARK`. */
  kind: z.string(),
  distanceKm: z.number(),
  durationMinutes: z.number().nullable().default(null),
  isDriving: z.boolean().default(false),
  isFeatured: z.boolean(),
  /**
   * Where the landmark actually is.
   *
   * Held on `city_landmarks` all along and dropped on the way out, which left
   * the property page able to say "0.8 km from Taurus Canteen" and unable to
   * show a visitor *which direction* that is. A distance without a bearing
   * answers half the question people open a map for: two hotels both "1 km from
   * the station" are a different proposition if one is across the river.
   *
   * Nullable because the pair travels together — a row with one of the two is a
   * row we cannot plot — and because the results card's copy of this shape is
   * built from a leaner query on some paths.
   */
  latitude: z.number().nullable().default(null),
  longitude: z.number().nullable().default(null),
});
export type HotelNearestLandmark = z.infer<typeof HotelNearestLandmarkSchema>;

/**
 * The closest railway / metro station, carried on its own rather than in
 * `nearestLandmarks`.
 *
 * **Why separate.** STATION rows are deliberately kept out of the landmark chip
 * list, and for a good reason: OSM names a lot of Indian and Gulf metro stops
 * after whoever sponsored them ("Life Pharmacy Metro"), which reads as noise
 * among "Gateway of India" and "Phoenix Mall". That objection is about a *chip
 * competing for a slot in a general list* — it says nothing about the fact
 * itself, which for anyone booking in Delhi, Mumbai or Bengaluru is often the
 * single most decisive thing about a property's location.
 *
 * On its own field it has a fixed place in the UI, is labelled as transit, and
 * never takes a slot from a landmark. Null when the city has no station data or
 * nothing sits close enough to be worth walking to.
 */
export const HotelNearestStationSchema = z.object({
  name: z.string(),
  /** Same reasoning as the landmark's — a station is only useful with a bearing. */
  latitude: z.number().nullable().default(null),
  longitude: z.number().nullable().default(null),
  /**
   * Straight-line kilometres. Deliberately *not* presented as a walking route:
   * stations rarely have a cached OSRM row, and inventing a road distance we
   * did not measure is how a five-minute claim turns into a fifteen-minute walk.
   * The UI derives an honest, clearly-hedged estimate from this.
   */
  distanceKm: z.number(),
});
export type HotelNearestStation = z.infer<typeof HotelNearestStationSchema>;

/**
 * How far a property is from its city's central railway station.
 *
 * Distinct from `nearestStation` (the closest metro / rail stop worth walking
 * to): this is the one mainline station per city - auto-picked from
 * OpenStreetMap, overridable in the City master - that a traveller arriving by
 * train actually uses. Null when the city has no railway (Dubai, Bali).
 *
 * `distanceKm` is an OSRM driving distance when `isDriving` (and
 * `durationMinutes` is set); straight-line otherwise, until the background
 * routing worker has filled the pair in.
 */
export const HotelCentralStationSchema = z.object({
  name: z.string(),
  latitude: z.number().nullable().default(null),
  longitude: z.number().nullable().default(null),
  distanceKm: z.number(),
  durationMinutes: z.number().nullable().default(null),
  isDriving: z.boolean().default(false),
});
export type HotelCentralStation = z.infer<typeof HotelCentralStationSchema>;

/**
 * The nearest commercial airport (one with an IATA code) and how far it is.
 * Same distance semantics as `HotelCentralStationSchema`.
 */
export const HotelNearestAirportSchema = z.object({
  name: z.string(),
  /** "DXB", "BOM". */
  iataCode: z.string(),
  latitude: z.number().nullable().default(null),
  longitude: z.number().nullable().default(null),
  distanceKm: z.number(),
  durationMinutes: z.number().nullable().default(null),
  isDriving: z.boolean().default(false),
});
export type HotelNearestAirport = z.infer<typeof HotelNearestAirportSchema>;

export const TboHotelLiveOptionSchema = z.object({
  hotelCode: z.string(),
  name: z.string(),
  starRating: z.number().int().nullable(),
  /**
   * 2026-07-05. Address + cover photo enriched from the local
   * `tbo_hotels` mirror by joining on `hotelCode` after the live
   * search returns. TBO's search response does NOT carry these
   * details (only `hotelCode` + price + refundable + resultIndex);
   * the mirror is the source for the rich card fields (name, star,
   * address, cover photo). Null when the hotel is in the live
   * response but not yet mirrored - fall back to the fallback
   * placeholder in the UI.
   */
  address: z.string().nullable(),
  /**
   * Reverse-geocoded neighborhood name from OpenStreetMap Nominatim,
   * cached on `tbo_hotels.neighborhood_name`. Powers the "Located in
   * Downtown Dubai" chip on hotel cards. Null when we haven't
   * geocoded this hotel yet or Nominatim returned nothing useful.
   */
  neighborhoodName: z.string().nullable().default(null),
  /**
   * Top nearest categorised landmarks (from `city_landmarks`), sorted by
   * distance ascending. Populated when the destination City has landmarks
   * imported. Empty otherwise. Distances in kilometres.
   *
   * `distanceKm` is the OSRM driving distance when `isDriving = true` (and
   * `durationMinutes` is populated); Haversine straight-line otherwise
   * (`durationMinutes = null`). Missing route pairs fall back to Haversine
   * so the chip always shows something.
   */
  nearestLandmarks: z.array(HotelNearestLandmarkSchema).default([]),
  /** Closest metro / railway station. See `HotelNearestStationSchema`. */
  nearestStation: HotelNearestStationSchema.nullable().default(null),
  /** Distance to the city's central railway station. See `HotelCentralStationSchema`. */
  centralStation: HotelCentralStationSchema.nullable().default(null),
  /** Nearest commercial airport + distance. See `HotelNearestAirportSchema`. */
  nearestAirport: HotelNearestAirportSchema.nullable().default(null),
  coverPhotoUrl: z.string().nullable(),
  /**
   * Cheapest room rate found, in the response currency. Encoded as a
   * numeric string ("paise as text") to keep JSON-Schema / Swagger emit
   * working with nestjs-zod (z.bigint() blows up at startup with
   * "BigInt cannot be represented in JSON Schema"). Matches the
   * packages module's convention.
   */
  pricePaise: z.string().regex(/^-?\d+$/, 'pricePaise must be a whole-number string'),
  /**
   * The hotel's own tax inside `pricePaise`, same currency, when TBO breaks it
   * out. Null/absent when it does not - the whole price is then room rate.
   */
  taxPaise: z
    .string()
    .regex(/^-?\d+$/)
    .nullable()
    .optional(),
  currencyCode: z.string(),
  /** Whether at least one rate in the response is refundable. */
  isRefundable: z.boolean(),
  /** TBO's opaque traceId for the subsequent prebook/book flow. */
  resultIndex: z.string(),
});
export type TboHotelLiveOption = z.infer<typeof TboHotelLiveOptionSchema>;

export const TboHotelLiveSearchResponseSchema = z.object({
  options: z.array(TboHotelLiveOptionSchema),
  traceId: z.string().nullable(),
});
export type TboHotelLiveSearchResponse = z.infer<typeof TboHotelLiveSearchResponseSchema>;

// ---- Room-level options for one hotel (detailed Search) -----------------

export const TboHotelRoomsInputSchema = z.object({
  /** The TBO HotelCode to fetch rooms for. */
  hotelCode: z.string().trim().min(1),
  checkIn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
  checkOut: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
  paxRooms: z.array(TboPaxRoomSchema).min(1).max(8),
  guestNationality: z.string().trim().length(2).default('IN'),
  /** Currency the prices should be returned in (converted live). Default INR. */
  currencyCode: z.string().trim().length(3).default('INR'),
});
export type TboHotelRoomsInput = z.infer<typeof TboHotelRoomsInputSchema>;

/**
 * One sub-room inside a TBO combo offer. When PaxRooms has N entries
 * (multi-room booking) TBO returns each Rooms[i] as ONE BookingCode with a
 * `Name` array of length N - element `k` is the offered room for
 * PaxRooms[k]. `subRooms` exposes that structurally so the UI can show
 * "Room 1: Std King / Room 2: Std Twin" instead of a comma-joined blob.
 */
export const TboHotelSubRoomSchema = z.object({
  name: z.string(),
  mealType: z.string().nullable(),
});
export type TboHotelSubRoom = z.infer<typeof TboHotelSubRoomSchema>;

export const TboHotelRoomOptionSchema = z.object({
  /** TBO BookingCode for this rate - needed for prebook/book later. */
  bookingCode: z.string().nullable(),
  /** Joined label kept for backwards-compat call sites; prefer `subRooms`. */
  name: z.string(),
  mealType: z.string().nullable(),
  isRefundable: z.boolean(),
  /** TOTAL fare across every sub-room in this combo offer (paise text). */
  pricePaise: z.string().regex(/^-?\d+$/, 'pricePaise must be a whole-number string'),
  currencyCode: z.string(),
  /**
   * One entry per PaxRooms block sent in the search request. Always has
   * length >= 1 (we fall back to a single entry derived from `name` when
   * TBO returns a scalar Name). Order matches the PaxRooms request order.
   */
  subRooms: z.array(TboHotelSubRoomSchema).min(1),
  /**
   * Date-banded cancellation terms for this rate, as returned by the detailed
   * Search (`CancelPolicies[]`). Defaults to empty so the pre-existing admin
   * callers - which never read it - keep parsing unchanged.
   *
   * This is what the traveller agrees to, so it is rendered as a timeline on
   * the room card and pinned onto the booking at PreBook. See
   * `HotelCancellationPolicySchema` in `hotel-booking.ts` for the booking-side
   * snapshot, which is the authoritative copy once a session exists.
   */
  cancellationPolicy: HotelCancellationPolicySchema.nullable().default(null),
  /** What the rate covers, as the supplier words it. Raw supplier text. */
  inclusion: z.string().nullable().default(null),
  /** Rate-plan / board-basis label when the supplier publishes one separately. */
  ratePlanName: z.string().nullable().default(null),
});
export type TboHotelRoomOption = z.infer<typeof TboHotelRoomOptionSchema>;

export const TboHotelRoomsResponseSchema = z.object({
  rooms: z.array(TboHotelRoomOptionSchema),
});
export type TboHotelRoomsResponse = z.infer<typeof TboHotelRoomsResponseSchema>;

// =========================================================================
// Public website surface (`/hotels/*`)
// =========================================================================
//
// Distinct from the admin shapes above on purpose. The admin picker wants TBO's
// own vocabulary and the contract behind a rate; the website wants a shape a
// guest can read.
//
// **Prices on this surface are the property's own rate.** Our markup, the
// convenience fee and the GST on both are charged, but they appear once, at
// checkout, as the single "Convenience Fees" line - so a figure here is what the
// hotel charges, and the total rises at the last step. These fields used to
// carry the sell price; if you are adding a surface, quote what you are given
// and do not add a ladder on top.

// ---- Destination autocomplete -------------------------------------------

/**
 * A bookable hotel destination.
 *
 * Keyed by TBO's `CityId`, not by IATA code. The flight widget's city lookup
 * returns airports and is the wrong source here: plenty of hotel destinations
 * have no airport, and plenty of airports serve a city TBO files under a
 * different id.
 */
export const HotelCityItemSchema = z.object({
  /** TBO `CityId`. This is what a search request carries. */
  cityId: z.string(),
  name: z.string(),
  countryCode: z.string().nullable(),
  countryName: z.string().nullable(),
  /** How many hotels the local mirror holds. Drives the ordering. */
  hotelCount: z.number().int().min(0).default(0),
});
export type HotelCityItem = z.infer<typeof HotelCityItemSchema>;

export const HotelCityQuerySchema = z.object({
  q: z.string().trim().max(80).optional(),
  limit: z.coerce.number().int().min(1).max(25).optional().default(10),
});
export type HotelCityQuery = z.infer<typeof HotelCityQuerySchema>;

export const HotelCityListResponseSchema = z.object({
  data: z.array(HotelCityItemSchema),
});
export type HotelCityListResponse = z.infer<typeof HotelCityListResponseSchema>;

/**
 * A named property offered in the destination dropdown.
 *
 * TBO has no "search hotels by name" method - `HotelSearch` is keyed on hotel
 * codes and nothing else - so this is matched against the local mirror
 * (`tbo_hotels`), which is the only place a hotel's *name* exists on our side.
 * That has one consequence worth stating: **only mirrored properties are
 * findable by name.** A city nobody has searched yet has no rows, so it
 * contributes no suggestions until the city sync has run for it.
 *
 * Carries `cityId` because it is not decoration: every stay URL is keyed on the
 * TBO city, so picking a property still has to produce one.
 */
export const StayHotelSuggestionSchema = z.object({
  /** TBO `HotelCode`. Addresses the property page. */
  hotelCode: z.string(),
  name: z.string(),
  starRating: z.number().int().min(0).max(5).nullable(),
  /** TBO `CityId` the property sits in - the stay URL is keyed on it. */
  cityId: z.string(),
  cityName: z.string(),
  countryCode: z.string().nullable(),
  countryName: z.string().nullable(),
  /** First mirrored photo, when there is one. Not health-checked - the
   *  dropdown falls back to an icon rather than blocking on a HEAD probe. */
  thumbnailUrl: z.string().nullable(),
});
export type StayHotelSuggestion = z.infer<typeof StayHotelSuggestionSchema>;

/**
 * One row of the destination dropdown: a city to search, or a property to open.
 *
 * A single ranked list rather than two, because a visitor typing "taj" does not
 * know or care which of the two kinds their answer is filed under - they want
 * the one list, ordered by what is most likely meant.
 *
 * Discriminated on `type` so the caller cannot read a `hotelCode` off a city by
 * accident. The `city` variant is `HotelCityItem` plus the tag, which is what
 * keeps the older cities-only readers of this endpoint working unchanged.
 */
export const StayDestinationSchema = z.discriminatedUnion('type', [
  HotelCityItemSchema.extend({ type: z.literal('city') }),
  StayHotelSuggestionSchema.extend({ type: z.literal('hotel') }),
]);
export type StayDestination = z.infer<typeof StayDestinationSchema>;

export const StayDestinationListResponseSchema = z.object({
  data: z.array(StayDestinationSchema),
});
export type StayDestinationListResponse = z.infer<typeof StayDestinationListResponseSchema>;

// ---- Search -------------------------------------------------------------

export const HotelSearchInputSchema = z
  .object({
    /** TBO `CityId` from the autocomplete above. */
    cityId: z.string().trim().min(1),
    checkIn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
    checkOut: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
    rooms: z.array(HotelRoomOccupancyInputSchema).min(1).max(8),
    /** ISO-2. Materially changes price and availability - never hardcode it. */
    guestNationality: z.string().trim().length(2).default('IN'),
    currencyCode: z.string().trim().length(3).default('INR'),
    /** Mirrors the legacy policy of never quoting 1-2 star inventory. */
    minStarRating: z.number().int().min(0).max(5).default(3),
    /**
     * A property the visitor named, which must be priced whatever else is.
     *
     * Set when someone searched a hotel by name rather than a city. Without it
     * the feature is unreliable rather than merely imperfect: a city search
     * prices a bounded slice of its inventory, ordered by star rating, so a
     * named mid-range hotel in a large city is quite likely to fall outside the
     * slice and simply not appear in its own search results.
     *
     * It changes which codes get priced, so it belongs to the REQUEST - and
     * therefore to the cache key - and not only to the page that draws the
     * highlight.
     */
    focusHotelCode: z.string().trim().min(1).max(32).optional(),
  })
  .refine((v) => v.checkOut > v.checkIn, {
    message: 'Check-out must be after check-in',
    path: ['checkOut'],
  });
export type HotelSearchInput = z.infer<typeof HotelSearchInputSchema>;

/**
 * One property in the results list, already de-duplicated.
 *
 * **De-duplication is mandatory**, not cosmetic: TBO aggregates several
 * suppliers and returns the same physical hotel more than once at different
 * prices, and shipping those as separate cards blocks go-live certification.
 * Grouping happens server-side on `hotelCode`; the cheapest rate becomes the
 * headline and `alternateRateCount` says how many others were folded in.
 */
export const HotelSearchResultSchema = z.object({
  hotelCode: z.string(),
  name: z.string(),
  starRating: z.number().int().nullable(),
  address: z.string().nullable(),
  neighborhoodName: z.string().nullable().default(null),
  coverPhotoUrl: z.string().nullable(),
  /**
   * A few more of the property's photos, for the card's carousel - the mirror's
   * list, deduped and capped. `coverPhotoUrl` is normally the first of them; the
   * card leads with it either way. Optional so an older API response parses, and
   * empty for a property whose detail row has not synced.
   */
  photoUrls: z.array(z.string()).optional(),
  latitude: z.number().nullable().default(null),
  longitude: z.number().nullable().default(null),
  nearestLandmarks: z.array(HotelNearestLandmarkSchema).default([]),
  /** Closest metro / railway station. See `HotelNearestStationSchema`. */
  nearestStation: HotelNearestStationSchema.nullable().default(null),
  /** Distance to the city's central railway station. See `HotelCentralStationSchema`. */
  centralStation: HotelCentralStationSchema.nullable().default(null),
  /** Nearest commercial airport + distance. See `HotelNearestAirportSchema`. */
  nearestAirport: HotelNearestAirportSchema.nullable().default(null),
  /**
   * The property's own rate for the whole stay, all rooms, its taxes included.
   *
   * Browsing reads it through `lib/hotels/list-price.ts`. Nothing of ours is in it.
   */
  supplierPricePaise: z.string().regex(/^-?\d+$/),
  /**
   * `supplierPricePaise` per room-night - the hotel's per-night figure, its
   * taxes included, ours not. The browsing headline is `roomRatePerNightPaise`;
   * this minus that is the "+₹X taxes & fees" line.
   */
  supplierPerNightPaise: z
    .string()
    .regex(/^-?\d+$/)
    .optional(),
  /** The base fare alone, per room-night - the browsing headline. Optional: older API. */
  roomRatePerNightPaise: z
    .string()
    .regex(/^-?\d+$/)
    .optional(),
  /**
   * Our own charge for the whole stay: markup + GST on it, pre-summed.
   *
   * One field rather than the three-row ladder because that is what a guest is
   * owed - a single figure for what the agent takes. The components stay split
   * server-side for the invoice; see `convenienceFeeTotalPaise`.
   */
  convenienceFeePaise: z.string().regex(/^-?\d+$/),
  /**
   * What the stay costs, all in: `supplierPricePaise + convenienceFeePaise`.
   *
   * This is the figure checkout charges. Search used to quote the supplier rate
   * alone and let the total rise at the payment step; it no longer does, so the
   * number on the card is the number on the gateway.
   */
  totalPricePaise: z.string().regex(/^-?\d+$/),
  /** The same figure divided by nights and rooms, for the headline "/night". */
  perNightPricePaise: z.string().regex(/^-?\d+$/),
  currencyCode: z.string(),
  isRefundable: z.boolean(),
  /** Other rates for this same property that de-dupe folded into this card. */
  alternateRateCount: z.number().int().min(0).default(0),
  /**
   * What KIND of property this is - "Resort", "Apartment", "Hostel".
   *
   * A closed vocabulary (`PROPERTY_TYPES` in the API's `hotel-facets`), not the
   * supplier's free text: TBO spells the same classification four ways and a
   * filter built on the raw strings offers four chips that each hide the other
   * three's properties.
   *
   * Null when the mirror carries no category for the property, or carries one
   * nothing can be made of. Deliberately never guessed from the property NAME -
   * "Sea Breeze Resort" is not evidence of a classification, and a type filter
   * that guesses shows a guest inventory they ruled out.
   */
  propertyType: z.string().nullable().default(null),
  /**
   * Canonical amenity tags for the property, from the same closed vocabulary
   * treatment (`AMENITY_TAGS`).
   *
   * Facets, not a feature list: enough to answer "pool, parking, gym" in the
   * filter rail. The property page's full amenity list still comes from
   * `HotelPublicDetail.amenities`, unnormalised and complete.
   *
   * Empty for a property whose detail row has not been synced yet, which is why
   * the rail treats an amenity as narrowing rather than as a claim about
   * everything it excludes.
   */
  amenityTags: z.array(z.string()).default([]),
  /**
   * Room amenities, views and house rules - the same closed-vocabulary
   * treatment (`hotel-facets`), read from the same supplier facility list. TBO
   * has no separate field for any of them, so a tag means "the property lists
   * it", not "every room has it". Optional so an older API response parses.
   */
  roomAmenityTags: z.array(z.string()).optional(),
  viewTags: z.array(z.string()).optional(),
  houseRuleTags: z.array(z.string()).optional(),
});
export type HotelSearchResult = z.infer<typeof HotelSearchResultSchema>;

export const HotelSearchResponseSchema = z.object({
  results: z.array(HotelSearchResultSchema),
  nights: z.number().int().min(1),
  roomCount: z.number().int().min(1),
  currencyCode: z.string(),
  /** Properties TBO priced before de-duplication, for "N of M" copy. */
  totalBeforeDedupe: z.number().int().min(0),
});
export type HotelSearchResponse = z.infer<typeof HotelSearchResponseSchema>;

// ---- Property detail + rooms --------------------------------------------

export const HotelPublicDetailSchema = z.object({
  hotelCode: z.string(),
  name: z.string(),
  starRating: z.number().int().nullable(),
  address: z.string().nullable(),
  neighborhoodName: z.string().nullable().default(null),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  /** Raw supplier HTML. Sanitised server-side before it reaches this field. */
  description: z.string().nullable().default(null),
  amenities: z.array(z.string()).default([]),
  photos: z.array(z.string()).default([]),
  checkInTime: z.string().nullable().default(null),
  checkOutTime: z.string().nullable().default(null),
  /** TBO's own facet — "Resort", "Apartment", "Heritage". Free text. */
  category: z.string().nullable().default(null),
  /**
   * House rules the guest is agreeing to, one per line.
   *
   * Read from TBO's `HotelPolicy`, which arrives as one blob of HTML and line
   * breaks. Split into lines server-side and stripped of tags rather than
   * handed over as markup: these are the rules that get someone turned away at
   * the desk — unmarried couples, local ID, minimum age, pets — so they need to
   * render as a list a person actually reads, and a supplier string is not
   * something to inject into the page as HTML.
   *
   * Empty when the property publishes none. A property with no stated rules is
   * common and is not an error.
   */
  importantInfo: z.array(z.string()).default([]),
  /** The property's own reception number, when the supplier states one. */
  phone: z.string().nullable().default(null),
  /**
   * Nearest landmarks, same source as the results card but unclipped — the
   * property page has room to list what is around it, not just the top three.
   */
  nearestLandmarks: z.array(HotelNearestLandmarkSchema).default([]),
  /** Closest metro / railway station. See `HotelNearestStationSchema`. */
  nearestStation: HotelNearestStationSchema.nullable().default(null),
  /** Distance to the city's central railway station. See `HotelCentralStationSchema`. */
  centralStation: HotelCentralStationSchema.nullable().default(null),
  /** Nearest commercial airport + distance. See `HotelNearestAirportSchema`. */
  nearestAirport: HotelNearestAirportSchema.nullable().default(null),
});
export type HotelPublicDetail = z.infer<typeof HotelPublicDetailSchema>;

export const HotelRoomSearchInputSchema = z
  .object({
    checkIn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
    checkOut: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
    rooms: z.array(HotelRoomOccupancyInputSchema).min(1).max(8),
    guestNationality: z.string().trim().length(2).default('IN'),
    currencyCode: z.string().trim().length(3).default('INR'),
  })
  .refine((v) => v.checkOut > v.checkIn, {
    message: 'Check-out must be after check-in',
    path: ['checkOut'],
  });
export type HotelRoomSearchInput = z.infer<typeof HotelRoomSearchInputSchema>;

/** One bookable rate on the property page, at the property's own rate. */
export const HotelRoomRateSchema = z.object({
  /** Opaque carry-forward token. The only thing a session-create call needs. */
  bookingCode: z.string(),
  name: z.string(),
  mealType: z.string().nullable(),
  ratePlanName: z.string().nullable().default(null),
  inclusion: z.string().nullable().default(null),
  isRefundable: z.boolean(),
  /** One entry per requested room, in request order. */
  subRooms: z.array(TboHotelSubRoomSchema).min(1),
  /** The property's own rate for the stay, its taxes included. Ours excluded. */
  supplierPricePaise: z.string().regex(/^-?\d+$/),
  /** Per room-night figures, as on a search result - see its copy. Optional: older API. */
  supplierPerNightPaise: z
    .string()
    .regex(/^-?\d+$/)
    .optional(),
  /** The base fare alone, per room-night - the browsing headline. Optional: older API. */
  roomRatePerNightPaise: z
    .string()
    .regex(/^-?\d+$/)
    .optional(),
  /** Our markup plus the GST on it, pre-summed. See the search result's copy. */
  convenienceFeePaise: z.string().regex(/^-?\d+$/),
  /** All in - supplier + ours - and the same basis checkout charges. */
  totalPricePaise: z.string().regex(/^-?\d+$/),
  perNightPricePaise: z.string().regex(/^-?\d+$/),
  currencyCode: z.string(),
  cancellationPolicy: HotelCancellationPolicySchema.nullable().default(null),
});
export type HotelRoomRate = z.infer<typeof HotelRoomRateSchema>;

export const HotelRoomRatesResponseSchema = z.object({
  hotelCode: z.string(),
  rates: z.array(HotelRoomRateSchema),
  nights: z.number().int().min(1),
  roomCount: z.number().int().min(1),
  currencyCode: z.string(),
});
export type HotelRoomRatesResponse = z.infer<typeof HotelRoomRatesResponseSchema>;

// ---- Drive times from a place the visitor named -------------------------

/**
 * "How long is the drive from here to each of these hotels?"
 *
 * The map already answers "how far" as the crow flies, which is the wrong
 * question in most Indian cities: a property 2 km away across a river or a rail
 * line can be a forty-minute drive, and one 6 km away down an expressway can be
 * twelve. Straight-line distance quietly recommends the wrong hotel to anyone
 * choosing a stay around an office, a venue or a hospital.
 *
 * The origin is a coordinate the visitor produced by naming a place, and the
 * hotels are the ones currently on their map. Nothing is stored: this is a
 * question about one visitor's search, not a fact about the property.
 */
export const HotelDriveTimeInputSchema = z.object({
  origin: z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
  }),
  /**
   * Capped because each request becomes real calls to a routing engine. A
   * single city search returns ~100 properties, so this covers a whole map with
   * room to spare while keeping one visitor from asking for a country.
   */
  hotels: z
    .array(
      z.object({
        hotelCode: z.string().trim().min(1),
        latitude: z.number().min(-90).max(90),
        longitude: z.number().min(-180).max(180),
      }),
    )
    .min(1)
    .max(200),
});
export type HotelDriveTimeInput = z.infer<typeof HotelDriveTimeInputSchema>;

export const HotelDriveTimeSchema = z.object({
  hotelCode: z.string(),
  /**
   * Driving minutes, or null when the router could not connect the two points.
   *
   * Null is a real answer and must stay distinguishable from zero: a property
   * the router cannot reach is not a property that is next door. Callers show
   * these as unmeasured rather than sorting them to the top.
   */
  minutes: z.number().nullable(),
  /** Road distance in km — always longer than the crow-flies figure. */
  km: z.number().nullable(),
});
export type HotelDriveTime = z.infer<typeof HotelDriveTimeSchema>;

export const HotelDriveTimeResponseSchema = z.object({
  results: z.array(HotelDriveTimeSchema),
  /** Pairs the router declined to answer, so the UI can say so out loud. */
  unroutable: z.number().int().min(0),
});
export type HotelDriveTimeResponse = z.infer<typeof HotelDriveTimeResponseSchema>;

// ---------------------------------------------------------------------------
// City transport anchors (City master): central railway station + airports.
// ---------------------------------------------------------------------------

export const CityTransportStationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  stationType: z.string().nullable(),
  /** `OVERPASS` (auto-imported) | `MANUAL` (operator-entered or operator-picked). */
  source: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  /** Straight-line km from the city's own centre coordinates; null when the city has none. */
  distanceFromCentreKm: z.number().nullable(),
  isCentralStation: z.boolean(),
});
export type CityTransportStation = z.infer<typeof CityTransportStationSchema>;

export const CityTransportAirportSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  iataCode: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  distanceFromCentreKm: z.number().nullable(),
});
export type CityTransportAirport = z.infer<typeof CityTransportAirportSchema>;

export const CityTransportSchema = z.object({
  cityId: z.string().uuid(),
  /** When the OSM sync last ran; null = never synced. */
  syncedAt: z.string().nullable(),
  /** True when the city has coordinates - without them nothing can be synced. */
  hasCoordinates: z.boolean(),
  centralStationId: z.string().uuid().nullable(),
  /** Mainline candidates, nearest the city centre first. */
  stations: z.array(CityTransportStationSchema),
  airports: z.array(CityTransportAirportSchema),
});
export type CityTransport = z.infer<typeof CityTransportSchema>;

/**
 * Set a city's central station. Exactly one mode:
 *   - `{ mode: 'pick', landmarkId }` - one of the listed candidates
 *   - `{ mode: 'manual', name, latitude, longitude }` - a station OSM missed
 *   - `{ mode: 'auto' }` - drop the operator choice and re-run the auto-pick
 */
export const SetCentralStationInputSchema = z
  .object({
    mode: z.enum(['pick', 'manual', 'auto']),
    landmarkId: z.string().uuid().optional(),
    name: z.string().trim().min(2).max(120).optional(),
    latitude: z.coerce.number().min(-90).max(90).optional(),
    longitude: z.coerce.number().min(-180).max(180).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.mode === 'pick' && !v.landmarkId) {
      ctx.addIssue({ code: 'custom', path: ['landmarkId'], message: 'Pick a station' });
    }
    if (v.mode === 'manual') {
      if (!v.name)
        ctx.addIssue({ code: 'custom', path: ['name'], message: 'Station name is required' });
      if (v.latitude === undefined)
        ctx.addIssue({ code: 'custom', path: ['latitude'], message: 'Latitude is required' });
      if (v.longitude === undefined)
        ctx.addIssue({ code: 'custom', path: ['longitude'], message: 'Longitude is required' });
    }
  });
export type SetCentralStationInput = z.infer<typeof SetCentralStationInputSchema>;
